import React, { useState, useRef, useEffect } from 'react';
import { useThemeLanguage, LANGUAGES } from '../context/ThemeLanguageContext';

const FlagIcon = ({ code }) => {
  const flags = {
    fr: (
      <svg viewBox="0 0 640 480" className="w-full h-full">
        <g fillRule="evenodd" strokeWidth="1pt">
          <path fill="#fff" d="M0 0h640v480H0z"/>
          <path fill="#002654" d="M0 0h213.3v480H0z"/>
          <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
        </g>
      </svg>
    ),
    en: (
      <svg viewBox="0 0 640 480" className="w-full h-full">
        <path fill="#012169" d="M0 0h640v480H0z"/>
        <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0z"/>
        <path fill="#C8102E" d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z"/>
        <path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z"/>
        <path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z"/>
      </svg>
    ),
    it: (
      <svg viewBox="0 0 640 480" className="w-full h-full">
        <g fillRule="evenodd" strokeWidth="1pt">
          <path fill="#fff" d="M0 0h640v480H0z"/>
          <path fill="#009246" d="M0 0h213.3v480H0z"/>
          <path fill="#ce2b37" d="M426.7 0H640v480H426.7z"/>
        </g>
      </svg>
    ),
    es: (
      <svg viewBox="0 0 640 480" className="w-full h-full">
        <path fill="#c60b1e" d="M0 0h640v480H0z"/>
        <path fill="#ffc400" d="M0 120h640v240H0z"/>
      </svg>
    ),
    ru: (
      <svg viewBox="0 0 640 480" className="w-full h-full">
        <g fillRule="evenodd" strokeWidth="1pt">
          <path fill="#fff" d="M0 0h640v480H0z"/>
          <path fill="#0039a6" d="M0 160h640v320H0z"/>
          <path fill="#d52b1e" d="M0 320h640v160H0z"/>
        </g>
      </svg>
    ),
  };

  return flags[code] || null;
};

const LanguageSelector = ({ isScrolled }) => {
  const { language, setLanguage } = useThemeLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="language-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center w-9 h-9 rounded-full overflow-hidden border-2 transition-all duration-200 shadow-sm hover:shadow-md ${
          isScrolled 
            ? 'border-gray-300 hover:border-gray-400 bg-white' 
            : 'border-white/30 hover:border-white/50 bg-white/10 backdrop-blur-sm'
        }`}
        aria-label="Select language"
      >
        <div className="w-6 h-6 rounded-full overflow-hidden">
          <FlagIcon code={language} />
        </div>
      </button>

      {isOpen && (
        <div 
          data-testid="language-dropdown"
          className="absolute right-0 mt-2 py-2 w-14 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              data-testid={`language-option-${lang.code}`}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center justify-center py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 ${
                language === lang.code ? 'bg-gray-100 dark:bg-gray-700' : ''
              }`}
              title={lang.name}
            >
              <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-500 transition-all shadow-sm">
                <FlagIcon code={lang.code} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
